import React from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/Layout";
import { AlertTriangle } from "lucide-react";
import { LEGAL, LEGAL_PUBLISHABLE, LEGAL_MISSING } from "../config/legalEntity";

const DraftBanner = () =>
  LEGAL_PUBLISHABLE ? null : (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-5 flex gap-2 items-start" data-testid="legal-draft-banner">
      <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
      <div className="text-[12px] text-amber-800">
        <b>Bozza non definitiva.</b> Documento in finalizzazione: alcuni dati legali obbligatori
        ({LEGAL_MISSING.join(", ")}) devono ancora essere inseriti e il testo è soggetto a revisione legale
        prima della pubblicazione ufficiale.
      </div>
    </div>
  );

const Sec = ({ n, title, children }) => (
  <section className="mb-5" data-testid={`legal-sec-${n}`}>
    <h2 className="text-base font-semibold text-gray-900 mb-1.5">{n}. {title}</h2>
    <div className="text-sm text-gray-700 space-y-1.5 leading-relaxed">{children}</div>
  </section>
);

const LegalFooter = ({ current }) => (
  <div className="border-t border-gray-100 mt-8 pt-4 text-center text-xs text-gray-400 space-x-3" data-testid="legal-footer">
    {current !== "privacy" && <Link to="/privacy" className="underline" data-testid="legal-link-privacy">Privacy Policy</Link>}
    {current !== "termini" && <Link to="/termini" className="underline" data-testid="legal-link-termini">Termini di Servizio</Link>}
    <Link to="/elimina-account" className="underline" data-testid="legal-link-elimina">Elimina account</Link>
    <Link to="/" className="underline" data-testid="legal-link-home">Home</Link>
  </div>
);

const Titolare = () => (
  <div className="text-sm text-gray-700 space-y-1" data-testid="legal-titolare">
    <p><b>{LEGAL.brand}</b></p>
    <p>Ragione sociale: {LEGAL.ragioneSociale} ({LEGAL.formaGiuridica})</p>
    <p>Titolare: {LEGAL.titolare}</p>
    <p>Partita IVA: {LEGAL.partitaIva} · Codice Fiscale: {LEGAL.codiceFiscale}</p>
    <p>REA: {LEGAL.rea}</p>
    <p>Sede legale: {LEGAL.sedeLegale}</p>
    <p>Sede operativa ({LEGAL.brandShort}): {LEGAL.sedeOperativa}</p>
    <p>Email: <a href={`mailto:${LEGAL.email}`} className="text-violet-700 underline">{LEGAL.email}</a> · Tel: {LEGAL.telefono}</p>
    <p>PEC: {LEGAL.pec}</p>
  </div>
);

export function PrivacyPolicy() {
  return (
    <Page>
      <div className="max-w-2xl mx-auto pt-8 pb-16 px-1" data-testid="privacy-page">
        <h1 className="serif text-3xl mb-1">Privacy Policy</h1>
        <p className="text-xs text-gray-400 mb-5">Ultimo aggiornamento: {LEGAL.lastUpdated}</p>
        <DraftBanner />

        <Sec n="1" title="Titolare del trattamento e contatti"><Titolare /></Sec>

        <Sec n="2" title="Dati trattati">
          <p>Trattiamo i dati necessari a fornire i nostri servizi:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Account/profilo</b>: nome, email, password (in forma cifrata/hash), ruolo, codice referral, consensi. Per gli artisti: professione, tecnica, profilo Instagram, foto profilo e portfolio, consenso alla pubblicazione in vetrina.</li>
            <li><b>Contatti</b>: email e numero di telefono.</li>
            <li><b>Autenticazione e sicurezza</b>: hash della password, token di sessione, reset password, log dei tentativi, protezione anti-abuso (identificatori pseudonimizzati, senza memorizzare l'IP in chiaro), registro delle azioni amministrative.</li>
            <li><b>Prenotazioni Coworking</b>: date, tipologia di postazione (Open Space / Cabina Privacy), stato, consenso alla pubblicazione della presenza.</li>
            <li><b>Pacchetti/crediti</b>: acquisti, saldo, scadenze.</li>
            <li><b>Resident Flex/Reserved</b>: abbonamenti, giorni prenotati, stato, rinnovi.</li>
            <li><b>Pagamenti</b>: gestiti tramite <b>Stripe</b>. I dati della carta non transitano né sono conservati dai nostri sistemi.</li>
            <li><b>Corsi/Eventi</b>: iscrizioni (nome, email, telefono), stato del pagamento.</li>
            <li><b>MAS Academy</b>: iscrizioni, avanzamento, esercizi, tutoraggio e relativi allegati (privati).</li>
            <li><b>Attestati</b>: certificati emessi che includono il nome dell'allievo come parte integrante del documento.</li>
            <li><b>Assistenza post-corso</b>: periodi di assistenza e stato.</li>
            <li><b>Referral/premi</b>: inviti, attribuzioni ed eventuali buoni.</li>
            <li><b>Foto e upload</b>: locandine, cover, foto profilo/portfolio (pubblici); foto della Tricopigmentazione caricate come lead (private, accessibili solo allo Studio).</li>
            <li><b>Log/analytics</b>: eventi d'uso e notifiche, per finalità di funzionamento e sicurezza del prodotto.</li>
          </ul>
        </Sec>

        <Sec n="3" title="Finalità e basi giuridiche">
          <ul className="list-disc pl-5 space-y-1">
            <li>Erogazione del servizio (account, prenotazioni, corsi, Academy, assistenza): <b>esecuzione del contratto</b>.</li>
            <li>Pagamenti e adempimenti fiscali/contabili: <b>obbligo legale</b>.</li>
            <li>Sicurezza, prevenzione abusi e audit: <b>legittimo interesse</b>.</li>
            <li>Email transazionali di servizio: contratto/legittimo interesse.</li>
            <li>Pubblicazione della presenza in vetrina e foto pubblicabili: <b>consenso</b>, revocabile in ogni momento.</li>
          </ul>
        </Sec>

        <Sec n="4" title="Fornitori e responsabili del trattamento">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Stripe</b> — elaborazione dei pagamenti.</li>
            <li><b>Resend</b> — invio delle email transazionali (servizio <b>attivo</b>).</li>
            <li><b>Twilio/WhatsApp</b> — notifiche via messaggistica: funzione <b>predisposta ma non attiva</b>. Sarà utilizzata solo previa informativa/consenso quando attivata.</li>
            <li>Applicazione mobile: in fase di sviluppo. Le eventuali <b>notifiche push</b> non sono ancora attive; se introdotte, tratteremo un identificativo del dispositivo e aggiorneremo questa informativa.</li>
          </ul>
          <p>Con i fornitori che trattano dati per nostro conto è (o sarà) in essere un accordo ai sensi dell'art. 28 GDPR.</p>
        </Sec>

        <Sec n="5" title="Tempi di conservazione">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Account</b>: per la durata del rapporto. In caso di richiesta di cancellazione si applica il ciclo descritto al §6.</li>
            <li><b>Dati economici/documentali</b> (pagamenti, registrazioni contabili, documenti): conservati per il tempo richiesto dagli obblighi di legge, anche dopo l'anonimizzazione dell'account.</li>
            <li><b>Foto della Tricopigmentazione</b>: le foto di un contatto (lead) <b>esplicitamente classificato come non convertito</b> vengono eliminate automaticamente dopo <b>90 giorni</b>. Le foto di chi diventa cliente vengono conservate come <b>documentazione del trattamento</b> secondo la relativa policy e non sono soggette alla cancellazione automatica.</li>
            <li><b>Attestati Academy emessi</b>: conservati integralmente, incluso il nome dell'allievo, quale parte del documento emesso, anche dopo l'anonimizzazione dell'account.</li>
            <li><b>Materiali/allegati Academy</b>: conservati coerentemente con il rapporto didattico.</li>
            <li><b>Log e dati di sicurezza</b>: conservazione limitata nel tempo.</li>
          </ul>
        </Sec>

        <Sec n="6" title="Cancellazione dell'account e anonimizzazione">
          <p>Puoi richiedere la cancellazione dall'area personale o dalla pagina pubblica <Link to="/elimina-account" className="text-violet-700 underline">Elimina account</Link>. Il processo prevede:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Sospensione immediata</b> dell'accesso al momento della conferma;</li>
            <li><b>14 giorni</b> di ripensamento, durante i quali puoi <b>annullare</b> la richiesta tramite il link ricevuto via email;</li>
            <li>trascorsi i 14 giorni, <b>anonimizzazione irreversibile</b> dei dati personali cancellabili (l'email viene sostituita con un identificativo anonimo).</li>
          </ul>
          <p>I dati economici/documentali dovuti per legge (es. attestati emessi, registrazioni contabili) restano conservati ove necessario, scollegati dall'identità quando possibile.</p>
        </Sec>

        <Sec n="7" title="Diritti dell'interessato">
          <p>Puoi esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità, opposizione e revoca del consenso, e proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it). Per le richieste: <a href={`mailto:${LEGAL.email}`} className="text-violet-700 underline">{LEGAL.email}</a>.</p>
        </Sec>

        <Sec n="8" title="Trasferimenti extra-UE">
          <p>Alcuni fornitori (es. Stripe, provider infrastrutturali) potrebbero trattare dati al di fuori dell'Unione Europea, adottando garanzie adeguate (es. Clausole Contrattuali Standard).</p>
        </Sec>

        <Sec n="9" title="Sicurezza">
          <p>Adottiamo hashing delle password, gestione sicura delle sessioni con invalidazione, protezione anti-abuso, controllo degli accessi ai file privati (foto Tricopigmentazione e materiali Academy) e trasmissione cifrata (HTTPS).</p>
        </Sec>

        <Sec n="10" title="Minori">
          <p>Il servizio è rivolto a professionisti e persone maggiorenni e non è destinato ai minori. Non raccogliamo consapevolmente dati di minori.</p>
        </Sec>

        <Sec n="11" title="Aggiornamenti">
          <p>La presente informativa può essere aggiornata; la versione vigente è pubblicata con la data di ultima modifica indicata in alto.</p>
        </Sec>

        <LegalFooter current="privacy" />
      </div>
    </Page>
  );
}

export function TerminiServizio() {
  return (
    <Page>
      <div className="max-w-2xl mx-auto pt-8 pb-16 px-1" data-testid="termini-page">
        <h1 className="serif text-3xl mb-1">Termini di Servizio</h1>
        <p className="text-xs text-gray-400 mb-5">Ultimo aggiornamento: {LEGAL.lastUpdated}</p>
        <DraftBanner />

        <Sec n="1" title="Titolare e contatti"><Titolare /></Sec>
        <Sec n="2" title="Account"><p>Per usare i servizi devi creare un account fornendo dati veritieri e aggiornati. Sei responsabile della custodia delle credenziali e delle attività svolte tramite il tuo account.</p></Sec>
        <Sec n="3" title="Uso lecito della piattaforma"><p>È vietato ogni uso illecito, l'accesso non autorizzato, la pubblicazione di contenuti illeciti o lesivi di diritti altrui.</p></Sec>
        <Sec n="4" title="Coworking"><p>Offriamo spazi di coworking (Open Space e Cabina Privacy) presso la sede operativa di {LEGAL.sedeOperativa}.</p></Sec>
        <Sec n="5" title="Prenotazioni"><p>Le prenotazioni sono soggette alla disponibilità reale. Le date e le postazioni prenotate sono vincolanti secondo le regole indicate in fase di prenotazione.</p></Sec>
        <Sec n="6" title="Modifiche/cancellazioni (policy 72h)"><p>Le modifiche o cancellazioni delle prenotazioni sono ammesse nei termini indicati (policy 72 ore) e possono incidere sulla restituzione di eventuali crediti.</p></Sec>
        <Sec n="7" title="Pacchetti e scadenze"><p>I pacchetti/crediti hanno una validità e una scadenza indicate all'acquisto. Salvo quanto previsto dalla legge, i crediti non utilizzati non sono rimborsabili.</p></Sec>
        <Sec n="8" title="Resident Flex/Reserved"><p>Gli abbonamenti Resident offrono condizioni dedicate; la Cabina Privacy, ove prevista, è soggetta a disponibilità e non è garantita. Sono previsti rinnovi e scadenze.</p></Sec>
        <Sec n="9" title="Corsi/Eventi"><p>L'iscrizione ai corsi/eventi si perfeziona con il pagamento secondo le modalità indicate.</p></Sec>
        <Sec n="10" title="Policy di rimborso corsi">
          <ul className="list-disc pl-5 space-y-1">
            <li>Annullamento con almeno <b>10 giorni</b> di anticipo: rimborso <b>80%</b>.</li>
            <li>Meno di 10 giorni ma almeno <b>72 ore</b>: rimborso <b>50%</b>.</li>
            <li>Meno di <b>72 ore</b>: <b>nessun rimborso</b>.</li>
            <li>Se il servizio non ha avuto inizio o è annullato dall'organizzatore: rimborso <b>100%</b>.</li>
          </ul>
        </Sec>
        <Sec n="11" title="MAS Academy"><p>I corsi Academy sono erogati con docenti selezionati sotto il marchio "MAS Academy".</p></Sec>
        <Sec n="12" title="Tutoraggio e assistenza post-corso"><p>Il perimetro, la durata e le modalità del tutoraggio e dell'assistenza post-corso sono indicati nel relativo corso.</p></Sec>
        <Sec n="13" title="Rimborsi assistenza"><p>Assistenza non ancora iniziata: rimborso del periodo annullato (100%). Assistenza già iniziata: nessun rimborso ordinario, salvo eccezioni valutate dallo Studio con motivazione.</p></Sec>
        <Sec n="14" title="Referral e premi"><p>Gli eventuali programmi referral e premi sono soggetti al relativo regolamento e alle condizioni di maturazione indicate.</p></Sec>
        <Sec n="15" title="Contenuti caricati dagli utenti"><p>Sei responsabile della liceità e della titolarità dei contenuti che carichi. Ci concedi una licenza d'uso limitata alla sola erogazione del servizio. Rimuoviamo i contenuti illeciti.</p></Sec>
        <Sec n="16" title="Proprietà intellettuale"><p>I marchi e i segni distintivi MAS, i contenuti dei corsi e i materiali dei docenti sono protetti e non possono essere utilizzati senza autorizzazione.</p></Sec>
        <Sec n="17" title="Attestati"><p>Gli attestati rilasciati sono attestati di frequenza/partecipazione ai corsi erogati e non costituiscono titoli di studio o abilitazioni, salvo ove diversamente ed espressamente indicato.</p></Sec>
        <Sec n="18" title="Limitazione di responsabilità"><p>Nei limiti consentiti dalla legge, la nostra responsabilità è limitata ai casi di dolo o colpa grave e comunque nei limiti inderogabili di legge.</p></Sec>
        <Sec n="19" title="Sospensione/cancellazione account"><p>L'account può essere sospeso o cancellato secondo le procedure descritte nella Privacy Policy (§6) e in caso di violazione dei presenti Termini.</p></Sec>
        <Sec n="20" title="Modifiche e legge applicabile"><p>I presenti Termini possono essere aggiornati con adeguato preavviso; si applica la versione vigente. Legge applicabile e foro: {LEGAL.foro}.</p></Sec>

        <LegalFooter current="termini" />
      </div>
    </Page>
  );
}
